import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import { debounce } from 'lodash';
import { computed, readonly, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';

export interface SearchResult {
    type: string;
    id: number;
    url: string;
    matches: Array<{
        field: string;
        value: string;
    }>;
    [key: string]: any; // For dynamic properties like name, email, etc.
}

export interface SearchResponse {
    results: SearchResult[];
    total: number;
}

export function useGlobalSearch() {
    const page = usePage();
    const queryClient = useQueryClient();
    const searchQuery = ref('');
    const searchLimit = ref(10);
    const debouncedQuery = ref('');
    const selectedCompanyId = computed<number | null>(() => page.props.auth?.user?.selected_company_id ?? null);
    const userId = computed<number | null>(() => page.props.auth?.user?.id ?? null);

    // Debounce the search query
    watch(
        searchQuery,
        debounce((q: string) => {
            debouncedQuery.value = q;
        }, 300),
        { immediate: true },
    );

    // Vue Query for search results
    const {
        data: searchResults,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: computed(() => [
            'global-search',
            userId.value,
            selectedCompanyId.value ?? 'no-company',
            debouncedQuery.value,
        ]),
        queryFn: async () => {
            const q = debouncedQuery.value;
            if (!q || q.length < 2) return { results: [], total: 0 };
            try {
                const { data } = await axios.get<SearchResponse>('/search', {
                    params: { query: q, limit: searchLimit.value },
                });
                console.log('Search API Response:', data); // Add this line
                return data;
            } catch (err) {
                console.error('Search failed:', err);
                return { results: [], total: 0 };
            }
        },
        enabled: computed(() => debouncedQuery.value.length >= 2),
        placeholderData: { results: [], total: 0 },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    watch(selectedCompanyId, (newCompanyId, previousCompanyId) => {
        if (previousCompanyId === undefined || newCompanyId === previousCompanyId) {
            return;
        }

        queryClient.removeQueries({
            queryKey: ['global-search', userId.value, previousCompanyId ?? 'no-company'],
        });

        searchQuery.value = '';
        debouncedQuery.value = '';
    });

    const setSearchQuery = (query: string) => {
        searchQuery.value = query;
    };

    const clearSearch = () => {
        searchQuery.value = '';
        debouncedQuery.value = '';
    };

    // Computed properties
    const hasResults = computed(() => {
        return searchResults.value?.results && searchResults.value.results.length > 0;
    });

    const isMinimumQuery = computed(() => {
        return debouncedQuery.value.length >= 2;
    });

    return {
        // State
        searchQuery: readonly(searchQuery),
        searchResults: readonly(searchResults),
        isLoading: readonly(isLoading),
        isFetching: readonly(isFetching),
        error: readonly(error),

        // Computed
        hasResults,
        isMinimumQuery,

        // Actions
        setSearchQuery,
        clearSearch,
        refetch,
    };
}
